import { CloseIcon } from "../../../../../assets/icon/svg";
import DropdownOption from "../../../../ui/dropdown-option";
import { useDispatch, useSelector } from "react-redux";
import { useActuarialAccess } from "../../../../../hooks/access/use-actuarial-access";
import { selectApproverRates, setApprover, setApproverNotes, setIsOpenApproverForm } from "../../../../../features/actuarial/approval/approval-slice";
import { useMemo, useState } from "react";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onRelease: () => void;
    isLoading: boolean;
}

export default function ComputationApprovalModal({ isOpen, onClose, onRelease, isLoading }: Props) {

    const {  actuarialTypeOptions, selectedApproverValue, isLoadingActuarialUsers, isErrorActuarialUsers, } = useActuarialAccess();
    const dispatch = useDispatch()
    const { approver, isOpen: isOpenModal, notes } = useSelector(selectApproverRates);
    const disabledBtn = approver?.id === 0 || approver === null

    const [searchApprover, setSearchApprover] = useState("");
    const filteredApproverOptions = useMemo(() => {
        const search = searchApprover.trim().toLowerCase();

        if (!search) return actuarialTypeOptions;

        return actuarialTypeOptions.filter((option) =>
            `${option.name ?? ""} ${option.email ?? ""}`
                .toLowerCase()
                .includes(search)
        );
    }, [actuarialTypeOptions, searchApprover]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-[3px]">
            <div className="w-full max-w-[540px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
                    <div className="flex min-w-0 items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 21a7.5 7.5 0 0 1 15 0" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="m17 15 1.5 1.5L21 14" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-semibold text-slate-800">
                                Release Proposal Computation
                            </h2>
                            <p className="mt-0.5 text-xs leading-5 text-slate-500">
                                Assign a reviewer to validate the computation
                                before releasing it.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={onClose}
                        aria-label="Close"
                        className="disabled:cursor-not-allowed ml-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                    >
                        <CloseIcon />
                    </button>
                </div>
                {/* Body */}
                <div className="space-y-5 px-5 py-5">
                    {/* Reviewer Section */}
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-[#186cb2]/10 text-[#186cb2]">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19a6 6 0 0 0-12 0" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 11a4 4 0 1 0 0-8" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 19a6 6 0 0 0-4-5.65" />
                                </svg>
                            </div>
                            <label className="text-xs font-semibold text-slate-700">
                                Computation Approver
                                <span className="ml-1 text-red-500">*</span>
                            </label>
                        </div>
                        {/* <div className="relative">
                            <DropdownOption
                                width="w-full" 
                                padding="px-3 py-2" 
                                options={actuarialTypeOptions}
                                isLoading={isLoadingActuarialUsers}
                                isError={isErrorActuarialUsers}
                                value={selectedApproverValue}
                                onChange={(e) => dispatch(setApprover(e))}
                                open={isOpenModal} 
                                setOpen={() => dispatch(setIsOpenApproverForm(!isOpenModal))}
                                hasSubtitle={true}
                            />
                        </div> */}
                        
<div className="relative">
    {isOpenModal && (
        <div className="mb-2">
            <div className="relative">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400">
                    <circle cx="11" cy="11" r="7" />
                    <path strokeLinecap="round" d="m16 16 4 4" />
                </svg>
                <input
                    type="text"
                    value={searchApprover}
                    onChange={(event) => setSearchApprover(event.target.value)}
                    placeholder="Search approver by name or email..."
                    className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#186cb2] focus:ring-2 focus:ring-[#186cb2]/10"
                />
            </div>
        </div>
    )}

    <DropdownOption
        width="w-full"
        padding="px-3 py-2"
        options={filteredApproverOptions}
        isLoading={isLoadingActuarialUsers}
        isError={isErrorActuarialUsers}
        value={selectedApproverValue}
        onChange={(e) => {
            dispatch(setApprover(e));
            setSearchApprover("");
        }}
        open={isOpenModal}
        setOpen={() => {
            dispatch(setIsOpenApproverForm(!isOpenModal));
            setSearchApprover("");
        }}
        hasSubtitle={true}
    />
</div>

                        {/* Selected Reviewer */}
                        {approver && (
                            <div className="mt-3 flex items-center gap-3 rounded-lg border border-[#186cb2]/15 bg-[#186cb2]/5 px-3.5 py-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#186cb2] text-sm font-semibold text-white">
                                    {approver.name.split(" ").map((name) => name.charAt(0)).slice(0, 2).join("")}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="truncate text-xs font-semibold text-slate-800">
                                            {approver.name}
                                        </p>
                                        <span className="shrink-0 rounded-full bg-[#186cb2]/10 px-2 py-0.5 text-[9px] font-semibold text-[#186cb2]">
                                            Approver
                                        </span>
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                                        <div className="flex items-center gap-1 text-[10px] text-slate-500">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3 w-3" >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 6.75A2.25 2.25 0 0 1 5.25 4.5h13.5A2.25 2.25 0 0 1 21 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 17.25V6.75Z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m3.5 6 8.5 6 8.5-6" />
                                            </svg>
                                            {approver.email ?? '-'}
                                        </div>
                                    </div>
                                </div>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 shrink-0 text-[#186cb2]" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" />
                                </svg>
                            </div>
                        )}
                    </div>
                    {/* Notes Section */}
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-slate-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5" >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3.75h7.5L19.5 9v11.25a.75.75 0 0 1-.75.75h-12a.75.75 0 0 1-.75-.75V4.5a.75.75 0 0 1 .75-.75Z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 3.75V9h5.25M9 13h6M9 16h4" />
                                    </svg>
                                </div>
                                <label
                                    htmlFor="release-notes"
                                    className="text-xs font-semibold text-slate-700"
                                >
                                    Notes
                                </label>
                            </div>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-400">
                                Optional
                            </span>
                        </div>
                        <div className="relative">
                            <textarea
                                id="release-notes"
                                value={notes}
                                maxLength={500}
                                onChange={(event) => dispatch(setApproverNotes(event.target.value))}
                                placeholder="Add notes or instructions for the approver..."
                                rows={4}
                                className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-xs leading-5 text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#186cb2] focus:ring-2 focus:ring-[#186cb2]/10"
                            />
                            <div className="pointer-events-none absolute bottom-2 right-3">
                                <span className="text-[10px] text-slate-400">
                                    {notes.length}/500
                                </span>
                            </div>
                        </div>
                    </div>
                    {/* Information */}
                    <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" >
                            <circle cx="12" cy="12" r="9" />
                            <path strokeLinecap="round" d="M12 10v5" />
                            <path strokeLinecap="round" d="M12 7.5h.01" />
                        </svg>
                        <p className="text-[10px] leading-4 text-amber-700">
                            The selected approver will be responsible for
                            reviewing the proposal computation and rates after
                            release.
                        </p>
                    </div>
                </div>
                {/* Footer */}
                <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="disabled:cursor-not-allowed disabled:bg-slate-100 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100 sm:w-auto"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={isLoading || disabledBtn}
                        onClick={onRelease}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#186cb2] px-4 py-2.5 text-xs font-semibold text-white transition hover:bg-[#186cb2]/90 disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
                    >
                        {isLoading ? (
                            <>
                                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                Submitting...
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m13 6 6 6-6 6" />
                                </svg>
                                Submit
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

{/* <ComputationApprovalModal
                isOpen={true}
                onClose={() => {}}
                onRelease={() => {}}
                isLoading={false}
            /> */}